<!--
  LoginView (/login, guestOnly): Anmeldung für Büro, Lehrkräfte, Bewerber:innen und Partner.
  Echtes Formular (Passwort-Manager/Autofill), Fehler am Formular statt Snackbar, Hinweis nach automatischer Abmeldung,
  nach dem Anmelden zurück zur gemerkten Seite (Deep-Link oder abgelaufene Sitzung).
-->
<template>
  <auth-shell>
    <form class="acq-card au-card soft-inputs" novalidate @submit.prevent="login">
      <div>
        <h1 class="au-title">Anmelden</h1>
        <p class="acq-muted mb-0">Für Büro, Lehrkräfte, Bewerber:innen und Partner.</p>
      </div>

      <div v-if="sessionExpired && !error" class="au-alert au-alert--info" role="status">
        <b>Du wurdest automatisch abgemeldet</b>
        Deine Sitzung ist abgelaufen. Nach dem Anmelden geht es auf der Seite weiter, auf der du warst.
      </div>
      <div v-if="error" :class="`au-alert--${error.tone}`" class="au-alert" role="alert">
        <b>{{ error.title }}</b>
        {{ error.text }}
      </div>

      <div class="au-field">
        <label class="acq-label acq-label--required" for="login-mail">E-Mail</label>
        <v-text-field id="login-mail" v-model="mail" :disabled="loading" :error="error && error.tone === 'bad'"
                      autocomplete="username" color="info" dense hide-details inputmode="email" name="username" outlined
                      type="email"/>
      </div>

      <div class="au-field">
        <label class="acq-label acq-label--required" for="login-password">Passwort</label>
        <v-text-field id="login-password" ref="password" v-model="password" :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                      :disabled="loading" :error="error && error.tone === 'bad'" :type="showPassword ? 'text' : 'password'"
                      autocomplete="current-password" color="info" dense hide-details name="password" outlined
                      @blur="capsLock = false" @keydown="checkCapsLock" @keyup="checkCapsLock"
                      @click:append="showPassword = !showPassword"/>
        <span v-if="capsLock" class="au-hint au-hint--warn" role="status">Feststelltaste ist an</span>
      </div>

      <div class="au-row">
        <label class="au-check"><input v-model="remember" type="checkbox"> E-Mail merken</label>
        <button class="au-link" type="button" @click="toForgotPassword">Passwort vergessen?</button>
      </div>

      <button v-if="error && error.locked" class="cta cta--primary cta--lg au-block" type="button" @click="toForgotPassword">
        Passwort zurücksetzen
      </button>
      <button v-else :disabled="loading" class="cta cta--primary cta--lg au-block" type="submit">
        <v-progress-circular v-if="loading" indeterminate size="16" width="2"/>
        Anmelden
      </button>
    </form>
  </auth-shell>
</template>

<script lang="ts">
import Vue from 'vue';
import store from '../store';
import router from '../router';
import {LoginApi} from '@/classes/api/login.api.class';
import {ClientHooks} from '@/hooks';
import {takeReturnPath, takeSessionExpired} from '@/helpers/sessionEnd.helper';
import {getRememberedMail, rememberMail, setPrefillMail, takePrefillMail} from '@/helpers/authFlow.helper';
import {LoginError, loginErrorFor} from '@/helpers/loginErrors.helper';
import AuthShell from '@/components/auth/AuthShell.vue';

export default Vue.extend({
  components: {AuthShell},
  data: () => {
    const remembered = getRememberedMail();
    return {
      mail: takePrefillMail() || remembered,
      password: '',
      remember: !!remembered,
      showPassword: false,
      capsLock: false,
      loading: false,
      error: null as LoginError | null,
      sessionExpired: takeSessionExpired(),
    };
  },
  methods: {
    checkCapsLock(event: KeyboardEvent) {
      if (typeof event.getModifierState === 'function') {
        this.capsLock = event.getModifierState('CapsLock');
      }
    },
    toForgotPassword() {
      setPrefillMail(this.mail);
      this.$router.push('/forgot-password').catch(() => undefined);
    },
    async login() {
      if (this.loading) {
        return;
      }
      if (!this.mail.trim() || !this.password) {
        this.error = {tone: 'bad', title: 'Bitte E-Mail und Passwort eingeben', text: 'Beide Felder sind Pflicht.'};
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        await ClientHooks.preLogin();
      } catch (e) {
        this.$$showSnackbar('Fehler beim Ausführen eines System-Hooks', 'error', e);
        this.loading = false;
        return;
      }
      try {
        await LoginApi.login(this.mail.trim(), this.password);
        rememberMail(this.remember ? this.mail : null);
        if (store.state.axios) {
          const resp = await LoginApi.checkLogin();
          if (resp) {
            store.commit('setOriginUserId', resp.originUserId);
            store.commit('setViewAsRole', resp.viewAsRole || null);
          }
          // Back to the page that needed the login (deep link) or that was open when the session expired
          const returnPath = takeReturnPath();
          if (store.state.routeCache) {
            router.push(store.state.routeCache.fullPath).catch(() => undefined);
            store.commit('setRouteCache', null);
          } else {
            router.push(returnPath || '/').catch(() => undefined);
          }
        }
        try {
          await ClientHooks.postLogin();
        } catch (e) {
          this.$$showSnackbar('Fehler beim Ausführen eines System-Hooks', 'error', e);
        }
      } catch (e) {
        this.error = loginErrorFor(e);
        this.password = '';
        this.$nextTick(() => ((this.$refs.password as Vue | undefined)?.$el.querySelector('input') as HTMLInputElement | null)?.focus());
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/auth/auth.css"></style>
